import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles,
    Camera,
    MessageCircle,
    LineChart,
    Wrench,
    TrendingUp,
    Calendar,
    Home as HomeIcon,
    BarChart3,
    Phone,
    ArrowRight,
    Star,
    Check,
} from "lucide-react";
import IncomeCalculator from "@/components/site/IncomeCalculator";
import FAQSection from "@/components/site/FAQSection";
import { Reveal, MaskedLines, Parallax, Marquee } from "@/components/site/motion";
import { motion } from "framer-motion";
import { getProperties } from "@/lib/api";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/a130831b-2e4c-48d8-a410-44e131be4ab0/images/ce00d0909f802ed7eed19d58b4f404bf31d47fc02f0723d130dfeeccff213e90.png";

const SIDE_IMG =
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SERVICES = [
    { icon: Sparkles, title: "Airbnb Management", text: "Full end-to-end management — listing, guests, cleans, the lot." },
    { icon: Wrench, title: "Styling & Furnishing", text: "Furniture, finishing touches and styling that look great in photos." },
    { icon: Camera, title: "Photography", text: "Professional real-estate and hotel-standard photos that show your property at its best." },
    { icon: MessageCircle, title: "Guest Communication", text: "Responsive guest support — target reply time under one hour." },
    { icon: LineChart, title: "Pricing Optimisation", text: "Dynamic pricing via PriceLabs to keep your nightly rate competitive." },
    { icon: HomeIcon, title: "Property Setup", text: "Listing creation, copy and pricing — from first photo to first guest." },
];

const TESTIMONIALS = [
    {
        quote: "Big shoutout to Leo's Luxury — super easy to deal with and takes all the stress out of Airbnb hosting. If you prioritise professionalism and proper handling of everything, Leo's Luxury is my top pick!",
        name: "Uncle Joe Developments",
        loc: "Google Review · 5 stars",
    },
    {
        quote: "Decent location, even better host. Made the whole process soo easy and hassle free.",
        name: "Feyai Banyankindagiye",
        loc: "Google Review · 5 stars",
    },
    {
        quote: "Our Airbnb host was absolutely fantastic — welcoming, responsive, and genuinely thoughtful throughout our stay. The space was exactly as described, if not better. We'd happily stay again.",
        name: "Levi Epafara",
        loc: "Airbnb Guest Review · 5 stars",
    },
];

const MANIFESTO = [
    { n: "01", t: "Free Consultation", d: "We look at your property, talk through your goals, and give you a tailored revenue estimate for your location." },
    { n: "02", t: "Property Setup", d: "We handle styling guidance, photography, listing copy and pricing — so your home is ready to host on day one." },
    { n: "03", t: "Sit Back & Earn", d: "We host on your behalf — guests, cleans, maintenance coordination — and send you a monthly summary with the payouts." },
];

const MARQUEE = ["Premium Management", "Melbourne's West", "Hotel-Grade Hosting", "Werribee", "Geelong", "Melton", "Truganina", "Five-Star Reviews", "Owner-Run"];

export default function Home() {
    const [properties, setProperties] = useState([]);

    useEffect(() => {
        getProperties().then(setProperties).catch(() => {});
    }, []);

    return (
        <div className="bg-night">
            {/* ── KINETIC HERO ─────────────────────────────────────────── */}
            <section
                className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden"
                data-testid="home-hero"
            >
                {/* structural gold glow */}
                <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-gold/[0.06] blur-[120px]" />

                <div className="max-w-[1300px] mx-auto px-6 md:px-12 relative z-10">
                    <div className="overflow-hidden">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="eyebrow mb-8 flex items-center gap-3"
                        >
                            <span className="w-10 h-px bg-gold/60 inline-block" />
                            Melbourne&rsquo;s Premium Choice
                        </motion.div>
                    </div>

                    <MaskedLines
                        as="h1"
                        trigger="load"
                        className="headline text-[42px] sm:text-[64px] md:text-[86px] lg:text-[104px] text-cream leading-[0.95] max-w-[16ch]"
                        lines={[
                            "Maximise your",
                            "property income",
                            <span key="l">with <span className="italic text-gold">luxury</span> hosting.</span>,
                        ]}
                        stagger={0.14}
                        delay={0.2}
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
                        className="text-cream/60 text-lg md:text-xl font-light max-w-xl leading-relaxed mt-10 mb-10"
                    >
                        We host your Melbourne property on Airbnb, VRBO and
                        Booking.com — handling listings, guests, cleans and
                        pricing — so you earn well without the daily lift.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="flex flex-wrap gap-4"
                    >
                        <Link to="/contact" className="btn-gold group" data-testid="hero-book-consultation">
                            Book Free Consultation
                            <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                        </Link>
                        <a href="#estimate" className="btn-ghost" data-testid="hero-get-estimate">
                            Get Rental Estimate
                        </a>
                    </motion.div>
                </div>

                {/* Parallax hero frame */}
                <motion.div
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.3, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-[1300px] mx-auto px-6 md:px-12 mt-16 md:mt-24 relative z-10"
                >
                    <div className="relative aspect-[16/9] overflow-hidden border border-line-dark">
                        <Parallax distance={140} className="absolute inset-0 -top-16 -bottom-16">
                            <img
                                src={HERO_IMG}
                                alt="Luxury Melbourne property"
                                className="w-full h-[128%] object-cover"
                            />
                        </Parallax>
                        <div className="absolute inset-0 bg-gradient-to-t from-night via-night/10 to-transparent" />
                        <div className="hidden md:flex absolute bottom-8 left-8 bg-night/80 backdrop-blur-md border border-gold/30 px-6 py-4 items-center gap-4">
                            <div className="flex gap-1 text-gold">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                                ))}
                            </div>
                            <div>
                                <div className="font-serif text-lg text-cream leading-none">5.0 / 5</div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-cream/50 mt-1">guest rating</div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* ── EDITORIAL MARQUEE ────────────────────────────────────── */}
            <section className="border-y border-line-dark py-6 md:py-8 bg-night-panel" data-testid="marquee-strip">
                <Marquee
                    speed={38}
                    testid="marquee-track"
                    items={MARQUEE.map((m, i) => (
                        <span key={i} className="flex items-center">
                            <span className="font-serif italic text-2xl md:text-4xl text-cream/85 px-8">{m}</span>
                            <span className="w-2 h-2 rotate-45 bg-gold shrink-0" />
                        </span>
                    ))}
                />
            </section>

            {/* ── THE DIFFERENCE ───────────────────────────────────────── */}
            <section className="py-28 md:py-40 bg-night" data-testid="difference-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
                        <div className="lg:col-span-6">
                            <div className="eyebrow mb-5">Why Choose Us</div>
                            <MaskedLines
                                as="h2"
                                trigger="view"
                                className="headline text-4xl md:text-6xl text-cream"
                                lines={["The Leo’s Luxury", <span key="d" className="italic text-gold">Difference</span>]}
                            />
                        </div>
                        <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                            <p className="text-cream/55 font-light text-lg leading-relaxed">
                                We&rsquo;re an owner-run business, so you get the same
                                person looking after your property end to end —
                                communication, hosting, problem-solving, the lot.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line-dark border border-line-dark">
                        {[
                            { icon: TrendingUp, t: "Better Income", d: "Short-stay hosting typically earns 30–60% more than a long-term lease — exact uplift depends on your suburb, property and season." },
                            { icon: Sparkles, t: "Hands-On Hosting", d: "Quick guest replies, careful cleans, and a watchful eye on the property. We treat your home the way we'd treat our own." },
                            { icon: Check, t: "Stress-Free", d: "We handle the listing, calendar, pricing, guests, cleans and maintenance coordination. You get a monthly summary — and the payouts." },
                        ].map((b, i) => (
                            <Reveal
                                key={b.t}
                                delay={i * 0.1}
                                className="bg-night p-10 md:p-12 group hover:bg-surface transition-colors duration-500"
                                data-testid={`difference-card-${i}`}
                            >
                                <div className="w-14 h-14 border border-gold/40 flex items-center justify-center mb-8 group-hover:bg-gold group-hover:border-gold transition-colors duration-500">
                                    <b.icon size={22} strokeWidth={1.2} className="text-gold group-hover:text-night transition-colors duration-500" />
                                </div>
                                <h3 className="font-serif text-3xl text-cream mb-4">{b.t}</h3>
                                <p className="text-cream/55 font-light leading-relaxed text-[15px]">{b.d}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── WHY AIRBNB ───────────────────────────────────────────── */}
            <section className="py-28 md:py-40 bg-surface" data-testid="why-airbnb-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-6">
                        <div className="eyebrow mb-5">Why Airbnb?</div>
                        <MaskedLines
                            as="h2"
                            trigger="view"
                            className="headline text-4xl md:text-6xl text-cream mb-8"
                            lines={["The Smart", <span key="i" className="italic text-gold">Investment Choice</span>]}
                        />
                        <p className="text-cream/55 font-light text-lg leading-relaxed mb-12 max-w-xl">
                            Short-stay hosting is one of the strongest ways to
                            earn from a property in Melbourne&rsquo;s west and the
                            Geelong corridor — when it&rsquo;s set up and managed
                            properly.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {[
                                { icon: BarChart3, t: "Better Returns", d: "Short-stays typically earn 30–60% more than a long-term lease (varies by property and suburb)." },
                                { icon: Calendar, t: "Flexible Use", d: "Block out personal stays anytime — your home is still yours." },
                                { icon: HomeIcon, t: "You Stay in Control", d: "Full access, full visibility. We host on your behalf, not in your place." },
                                { icon: TrendingUp, t: "Growing Demand", d: "Melbourne short-stay demand keeps growing — especially in the west." },
                            ].map((f, i) => (
                                <Reveal key={f.t} delay={i * 0.08} className="flex gap-4">
                                    <div className="shrink-0 w-10 h-10 border border-gold/40 flex items-center justify-center">
                                        <f.icon size={16} strokeWidth={1.2} className="text-gold" />
                                    </div>
                                    <div>
                                        <h4 className="font-serif text-xl text-cream mb-1">{f.t}</h4>
                                        <p className="text-sm text-cream/55 font-light leading-relaxed">{f.d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-6 relative">
                        <Reveal y={60} className="editorial-img aspect-[4/5] overflow-hidden border border-line-dark">
                            <img src={SIDE_IMG} alt="Modern home interior" className="w-full h-full object-cover" />
                        </Reveal>
                        <div className="absolute -bottom-6 -left-6 md:-left-10 bg-gold text-night px-8 py-6">
                            <div className="font-serif text-5xl md:text-6xl leading-none">30-60%</div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-night/70 mt-2 font-mono">More Revenue</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── MANIFESTO / PROCESS ──────────────────────────────────── */}
            <section className="py-28 md:py-40 bg-night-panel relative overflow-hidden" data-testid="process-section">
                <div className="grain absolute inset-0" />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="mb-20">
                        <div className="eyebrow text-gold mb-5">The Process</div>
                        <MaskedLines
                            as="h2"
                            trigger="view"
                            className="headline text-4xl md:text-6xl text-cream"
                            lines={["Three chapters to", <span key="g" className="italic text-gold">getting started.</span>]}
                        />
                    </div>
                    <div className="divide-y divide-line-dark border-y border-line-dark">
                        {MANIFESTO.map((s, i) => (
                            <Reveal
                                key={s.n}
                                delay={i * 0.08}
                                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 group"
                                data-testid={`step-${s.n}`}
                            >
                                <div className="md:col-span-3">
                                    <span className="font-serif text-7xl md:text-8xl text-cream/10 group-hover:text-gold/40 transition-colors duration-700 leading-none">
                                        {s.n}
                                    </span>
                                </div>
                                <h3 className="md:col-span-4 font-serif text-3xl md:text-4xl text-cream">{s.t}</h3>
                                <p className="md:col-span-5 text-cream/55 font-light leading-relaxed text-lg self-center">{s.d}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SERVICES ─────────────────────────────────────────────── */}
            <section className="py-28 md:py-40 bg-night" data-testid="services-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                        <div className="lg:col-span-7">
                            <div className="eyebrow mb-5">Our Services</div>
                            <MaskedLines
                                as="h2"
                                trigger="view"
                                className="headline text-4xl md:text-6xl text-cream"
                                lines={["Everything we do", "for your property."]}
                            />
                        </div>
                        <div className="lg:col-span-5 flex items-end">
                            <p className="text-cream/55 font-light text-lg leading-relaxed">
                                All the moving parts of hosting — handled in one
                                place, by people who care about the result.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-line-dark border border-line-dark">
                        {SERVICES.map((s, i) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 32 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "0px 0px -10% 0px" }}
                                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.08 }}
                            >
                                <Link
                                    to="/services"
                                    className="block h-full bg-night p-10 group hover:bg-surface transition-colors duration-500"
                                    data-testid={`service-card-${i}`}
                                >
                                    <s.icon size={26} strokeWidth={1} className="text-gold mb-6" />
                                    <h3 className="font-serif text-2xl text-cream mb-3 group-hover:text-gold transition-colors">{s.title}</h3>
                                    <p className="text-cream/55 font-light text-sm leading-relaxed mb-6">{s.text}</p>
                                    <span className="text-[11px] uppercase tracking-[0.22em] text-cream/50 flex items-center gap-2 group-hover:text-gold transition-colors font-mono">
                                        Learn more <ArrowRight size={12} className="transition-transform duration-500 group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PROPERTIES STRIP ─────────────────────────────────────── */}
            <section className="py-28 md:py-40 bg-surface" data-testid="properties-strip">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <div className="eyebrow mb-5">Portfolio</div>
                            <MaskedLines
                                as="h2"
                                trigger="view"
                                className="headline text-4xl md:text-6xl text-cream"
                                lines={["A look at the homes", "we host."]}
                            />
                        </div>
                        <Link
                            to="/properties"
                            className="link-underline text-[12px] uppercase tracking-[0.22em] text-cream hover:text-gold inline-flex items-center gap-2 font-mono"
                            data-testid="properties-preview-link"
                        >
                            View all properties <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {properties.slice(0, 3).map((p, i) => (
                            <Reveal
                                key={p.id}
                                delay={i * 0.1}
                                className="bg-night border border-line-dark group"
                                data-testid={`home-property-${p.id}`}
                            >
                                <div className="editorial-img aspect-[4/3] overflow-hidden">
                                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="p-7">
                                    <div className="eyebrow text-gold mb-3">Boutique short-stay</div>
                                    <h3 className="font-serif text-2xl text-cream mb-3 leading-tight group-hover:text-gold transition-colors">{p.name}</h3>
                                    <div className="text-xs uppercase tracking-[0.18em] text-cream/50 mb-4 font-mono">
                                        {p.bedrooms} Bed · {p.bathrooms} Bath · {p.guests} Guests
                                    </div>
                                    <div className="hairline opacity-40 mb-4" />
                                    <p className="text-sm font-light text-cream/55 leading-relaxed line-clamp-3">{p.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── TESTIMONIALS ─────────────────────────────────────────── */}
            <section className="py-28 md:py-40 bg-night" data-testid="testimonials-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <div className="eyebrow mb-5">Testimonials</div>
                        <MaskedLines
                            as="h2"
                            trigger="view"
                            className="headline text-4xl md:text-6xl text-cream mb-8"
                            lines={["What our owners", "& guests say."]}
                        />
                        <div className="inline-flex items-center gap-3 border border-gold/40 px-5 py-2.5">
                            <div className="flex gap-1 text-gold">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                                ))}
                            </div>
                            <span className="font-serif text-xl text-cream">5.0</span>
                            <span className="text-[11px] uppercase tracking-[0.22em] text-cream/50 font-mono">· Google &amp; Airbnb</span>
                        </div>
                        <div className="mt-6">
                            <a
                                href="https://share.google/DTcBxT6UEnHYqz7uV"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="google-reviews-link"
                                className="link-underline text-[12px] uppercase tracking-[0.22em] text-cream hover:text-gold font-mono"
                            >
                                Read more on Google →
                            </a>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <Reveal
                                key={i}
                                delay={i * 0.1}
                                className="bg-surface border border-line-dark p-10 flex flex-col"
                                data-testid={`testimonial-${i}`}
                            >
                                <div className="flex gap-1 text-gold mb-5">
                                    {Array.from({ length: 5 }).map((_, k) => (
                                        <Star key={k} size={14} fill="currentColor" strokeWidth={0} />
                                    ))}
                                </div>
                                <blockquote className="font-serif text-xl md:text-2xl leading-snug text-cream mb-6 flex-1">
                                    &ldquo;{t.quote}&rdquo;
                                </blockquote>
                                <figcaption>
                                    <div className="font-serif text-xl text-cream">{t.name}</div>
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-cream/50 mt-1 font-mono">{t.loc}</div>
                                </figcaption>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── INCOME CALCULATOR ────────────────────────────────────── */}
            <section id="estimate" className="py-28 md:py-40 bg-surface" data-testid="home-estimate">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <IncomeCalculator />
                </div>
            </section>

            <FAQSection />

            {/* ── BOTTOM CTA ───────────────────────────────────────────── */}
            <section className="py-28 md:py-40 bg-night-panel relative overflow-hidden" data-testid="bottom-cta">
                <div className="grain absolute inset-0" />
                <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-gold/[0.07] blur-[120px]" />
                <div className="relative max-w-[1000px] mx-auto px-6 md:px-12 text-center">
                    <div className="eyebrow text-gold mb-6">Start Earning Today</div>
                    <MaskedLines
                        as="h2"
                        trigger="view"
                        className="headline text-4xl md:text-7xl text-cream mb-8"
                        lines={["Get your free", <span key="r" className="italic text-gold">rental estimate.</span>]}
                    />
                    <p className="text-cream/60 font-light text-lg leading-relaxed max-w-2xl mx-auto mb-10">
                        Get a tailored estimate of what your property could earn
                        on Airbnb, VRBO and Booking.com — no obligations.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Link to="/contact" className="btn-gold" data-testid="bottom-cta-estimate">
                            Get Free Estimate
                        </Link>
                        <a href="tel:0469076071" data-testid="bottom-cta-call" className="btn-ghost-light">
                            <Phone size={14} strokeWidth={1.4} />
                            Call 0469 076 071
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
